import React from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle, XCircle } from "lucide-react";

const ImportMappingStep = ({ detectionResult, columnMapping, setColumnMapping }) => {
  const { t } = useTranslation();

  return (
  <div className="space-y-6">
    <div className="bg-muted/30 border border-border rounded-lg p-4">
      <h4 className="font-semibold text-foreground mb-2">{t("importEmployees.mapping.title")}</h4>
      <p className="text-sm text-muted-foreground">
        {t("importEmployees.mapping.descriptionStart")}
        {" "}
        {t("importEmployees.mapping.descriptionRequiredPrefix")} <span className="font-semibold">{t("importEmployees.mapping.bold")}</span> {t("importEmployees.mapping.descriptionRequiredSuffix")}
      </p>
    </div>

    {detectionResult.preview_data && detectionResult.preview_data.length > 0 && (
      <div className="bg-muted/50 border rounded-lg p-4">
        <h5 className="font-semibold text-foreground mb-2 text-sm">
          {t("importEmployees.mapping.previewTitle")}
        </h5>
        <div className="overflow-x-auto text-xs">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                {detectionResult.detected_columns.map((col, idx) => (
                  <th key={idx} className="px-2 py-1 text-left font-medium text-muted-foreground">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {detectionResult.preview_data.map((row, idx) => (
                <tr key={idx} className="border-b">
                  {detectionResult.detected_columns.map((col, colIdx) => (
                    <td key={colIdx} className="px-2 py-1 text-foreground">
                      {row[col] || '-'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}

    <div className="space-y-3">
      <h5 className="font-semibold text-foreground">{t("importEmployees.mapping.configTitle")}</h5>
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {detectionResult.detected_columns.map((csvColumn) => (
          <div key={csvColumn} className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
            <div className="flex-1">
              <p className="font-medium text-foreground text-sm">{csvColumn}</p>
              <p className="text-xs text-muted-foreground">{t("importEmployees.mapping.csvColumn")}</p>
            </div>
            <div className="text-gray-400">→</div>
            <div className="flex-1">
              <select
                className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background text-foreground"
                value={columnMapping[csvColumn] || ""}
                onChange={(e) => {
                  const newMapping = { ...columnMapping };
                  if (e.target.value) {
                    newMapping[csvColumn] = e.target.value;
                  } else {
                    delete newMapping[csvColumn];
                  }
                  setColumnMapping(newMapping);
                }}
              >
                <option value="">{t("importEmployees.mapping.ignoreColumn")}</option>
                {detectionResult.available_fields.map((field) => (
                  <option key={field.field} value={field.field}>
                    {field.label} {field.required && t("importEmployees.mapping.requiredOption")}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ))}
      </div>
    </div>

    <div className="bg-yellow-950/20 border border-yellow-900/40 rounded-lg p-4">
      <p className="text-sm text-muted-foreground">
        <strong>{t("importEmployees.mapping.requiredFields")}:</strong> {t("importEmployees.mapping.requiredFieldsList")}
      </p>
      {detectionResult.available_fields
        .filter(f => f.required)
        .map(field => {
          const isMapped = Object.values(columnMapping).includes(field.field);
          return (
            <div key={field.field} className="flex items-center gap-2 mt-2">
              {isMapped ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <XCircle className="h-4 w-4 text-red-600" />
              )}
              <span className={`text-sm ${isMapped ? 'text-green-400' : 'text-red-400'}`}>
                {field.label} {isMapped ? '✓' : t("importEmployees.mapping.notMapped")}
              </span>
            </div>
          );
        })}
    </div>
  </div>
  );
};

export default ImportMappingStep;
